/**
 * Pre-export validation for a demo mission. Enforces the DJI hardware
 * constraints that would otherwise cause Pilot 2 to reject the KMZ or,
 * worse, silently fly a malformed mission, plus the dock-level airspace
 * constraints (geofence containment, no-fly zone collisions):
 *
 *  - Recording lens must not appear in the same leg's interval-photo
 *    lens set (one lens cannot record video and capture stills at once).
 *  - Recording lens must not appear in the waypoint `'photo'` action
 *    payload set on either end of the leg.
 *  - Gimbal pitch must lie within [-90, 30].
 *  - Interval distance must be > 0 when interval enabled.
 *  - Interval enabled with empty lens set is meaningless.
 *  - When a `dock` is passed: every waypoint must lie inside the dock's
 *    geofence; no waypoint may sit inside a visible no-fly zone; no leg
 *    may cross a visible no-fly zone (with the NFZ's bufferM inflation).
 *
 * The result is a flat list of errors so the editor UI can attach each
 * one to the right leg / waypoint card without further interpretation.
 *
 * Pure / unit-testable — no React or store dependencies.
 */

import type {
  DemoMission,
  DemoWaypoint,
  DemoLegParams,
  DemoDockStation,
  DemoNoFlyZone,
} from '../components/dock-demo/types';
import { haversineM } from './geometry';

export type ValidationErrorScope =
  | { kind: 'leg'; waypointId: string; legIndex: number }
  | { kind: 'waypoint'; waypointId: string };

export interface ValidationError {
  /** Stable identifier so the UI can match render slots to errors. */
  code:
    | 'lens-conflict-interval'
    | 'lens-conflict-waypoint-photo'
    | 'pitch-out-of-range'
    | 'interval-distance-invalid'
    | 'interval-no-lenses'
    | 'waypoint-outside-geofence'
    | 'waypoint-inside-nfz'
    | 'route-crosses-nfz';
  scope: ValidationErrorScope;
  message: string;
}

/* ── Geometry helpers (pure) ─────────────────────────────────────────────
 *
 * Working frame for everything below is WGS84 lng/lat in degrees. Distance
 * math uses an equirectangular projection centered at a local reference
 * point — sufficient for the km-scale geofences / NFZs the planner deals
 * with, and avoids the cost / complexity of full geodesic math.
 */

const M_PER_DEG_LAT = 111_000;

/** Meters per degree of longitude at the given latitude (in degrees). */
function mPerDegLng(latDeg: number): number {
  return M_PER_DEG_LAT * Math.cos((latDeg * Math.PI) / 180);
}

/** Great-circle distance between two [lng,lat] points, in meters.
 *  Haversine — accurate at any earth scale, used for radius-fence checks.
 *  Canonical copy lives in geometry.ts (shared with guide-line navigation). */
export { haversineM };

/** Ray-casting point-in-polygon. Vertices are an open ring — closure is
 *  implicit. Returns true for points on the boundary in most cases but
 *  the boundary is a measure-zero set that callers shouldn't rely on. */
export function pointInPolygon(
  pt: [number, number],
  poly: [number, number][],
): boolean {
  if (poly.length < 3) return false;
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/** True if segments p1→p2 and p3→p4 cross. Uses the standard orientation
 *  test on signed cross products in lng/lat. Sufficient for collision
 *  detection at our scale; no need to switch to local meters since this
 *  is a sign test (no distance). */
function segmentsIntersect(
  p1: [number, number],
  p2: [number, number],
  p3: [number, number],
  p4: [number, number],
): boolean {
  const orient = (a: [number, number], b: [number, number], c: [number, number]) =>
    Math.sign((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));
  const o1 = orient(p1, p2, p3);
  const o2 = orient(p1, p2, p4);
  const o3 = orient(p3, p4, p1);
  const o4 = orient(p3, p4, p2);
  return o1 !== o2 && o3 !== o4;
}

/** Min distance in meters from a point to a segment. All three points are
 *  converted to a local meters frame centered at the segment's start point
 *  before the 2D projection. */
function pointToSegmentDistM(
  p: [number, number],
  a: [number, number],
  b: [number, number],
): number {
  const mPerDegLngLocal = mPerDegLng(a[1]);
  const px = (p[0] - a[0]) * mPerDegLngLocal;
  const py = (p[1] - a[1]) * M_PER_DEG_LAT;
  const bx = (b[0] - a[0]) * mPerDegLngLocal;
  const by = (b[1] - a[1]) * M_PER_DEG_LAT;
  const lenSq = bx * bx + by * by;
  if (lenSq === 0) return Math.hypot(px, py);
  let t = (px * bx + py * by) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const qx = t * bx;
  const qy = t * by;
  return Math.hypot(px - qx, py - qy);
}

/** True if segment a→b crosses or comes within `bufferM` of polygon `poly`.
 *  Handles a/b inside the polygon (segment buried inside), edge crossings,
 *  and (when bufferM > 0) near-misses where the segment passes close to an
 *  edge without crossing it. */
export function segmentIntersectsPolygon(
  a: [number, number],
  b: [number, number],
  poly: [number, number][],
  bufferM = 0,
): boolean {
  if (poly.length < 3) return false;
  if (pointInPolygon(a, poly) || pointInPolygon(b, poly)) return true;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    if (segmentsIntersect(a, b, poly[j], poly[i])) return true;
  }
  if (bufferM > 0) {
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      // Test all four endpoint→segment distances; the min is the seg-seg
      // distance whenever the segments don't intersect (which we already
      // ruled out above).
      const d = Math.min(
        pointToSegmentDistM(a, poly[j], poly[i]),
        pointToSegmentDistM(b, poly[j], poly[i]),
        pointToSegmentDistM(poly[j], a, b),
        pointToSegmentDistM(poly[i], a, b),
      );
      if (d < bufferM) return true;
    }
  }
  return false;
}

/** True if a [lng,lat] point lies inside the dock's geofence. False if the
 *  dock has no valid home (lng/lat both 0 — uninitialized). */
function waypointInsideGeofence(
  wp: [number, number],
  dock: DemoDockStation,
): boolean {
  if (dock.lng === 0 && dock.lat === 0) return true; // skip — dock not located yet
  if (dock.geofence.type === 'radius') {
    return haversineM([dock.lng, dock.lat], wp) <= dock.geofence.radiusM;
  }
  return pointInPolygon(wp, dock.geofence.coordinates);
}

/** Filter to visible NFZs only (NFZs with `visible !== false` are visible
 *  by default). */
function visibleNoFlyZones(dock: DemoDockStation): DemoNoFlyZone[] {
  return (dock.noFlyZones ?? []).filter((z) => z.visible !== false);
}

export function validateMission(
  mission: DemoMission,
  dock?: DemoDockStation | null,
): ValidationError[] {
  const errors: ValidationError[] = [];
  const wps = mission.waypoints;

  // ── Dock-scoped airspace checks (geofence + NFZs) ────────────────────
  // Both depend on the bound dock — skipped if no dock provided so the
  // function stays backwards-compatible for callers that haven't been
  // wired through yet.
  if (dock) {
    const nfzs = visibleNoFlyZones(dock);

    for (let i = 0; i < wps.length; i++) {
      const wp = wps[i];
      const wpPt: [number, number] = [wp.lng, wp.lat];

      if (!waypointInsideGeofence(wpPt, dock)) {
        errors.push({
          code: 'waypoint-outside-geofence',
          scope: { kind: 'waypoint', waypointId: wp.id },
          message: `WP${i + 1}: outside the dock's geofence.`,
        });
      }

      for (const nfz of nfzs) {
        if (pointInPolygon(wpPt, nfz.vertices)) {
          errors.push({
            code: 'waypoint-inside-nfz',
            scope: { kind: 'waypoint', waypointId: wp.id },
            message: `WP${i + 1}: placed inside no-fly zone "${nfz.name ?? 'unnamed'}".`,
          });
          // Only emit one NFZ-inside error per waypoint — repeating for
          // every overlapping NFZ would just noise up the validation list.
          break;
        }
      }
    }

    // Per-leg NFZ crossing — buffer comes from the NFZ itself.
    for (let i = 0; i < wps.length - 1; i++) {
      const a: [number, number] = [wps[i].lng, wps[i].lat];
      const b: [number, number] = [wps[i + 1].lng, wps[i + 1].lat];
      for (const nfz of nfzs) {
        if (segmentIntersectsPolygon(a, b, nfz.vertices, nfz.bufferM ?? 0)) {
          errors.push({
            code: 'route-crosses-nfz',
            scope: { kind: 'leg', waypointId: wps[i].id, legIndex: i },
            message: `Leg ${i + 1}→${i + 2}: crosses no-fly zone "${nfz.name ?? 'unnamed'}".`,
          });
          // One leg-crossing error per leg keeps the message stream
          // readable; the user fixes the route, the next NFZ shows up
          // on the next pass.
          break;
        }
      }
    }

    // Implicit home legs (dock home → first waypoint; last waypoint → dock
    // home). The route layer renders these, and the auto-router detours
    // them, so validation should flag the same conflicts. Skipped when the
    // dock has no location yet (lng/lat both 0). Errors are scoped to the
    // adjacent waypoint so the UI can attach them to the same first/last
    // card the legParams errors already use.
    const dockLocated = dock.lng !== 0 || dock.lat !== 0;
    if (dockLocated && wps.length >= 1) {
      const home: [number, number] = [dock.lng, dock.lat];
      const firstPt: [number, number] = [wps[0].lng, wps[0].lat];
      for (const nfz of nfzs) {
        if (segmentIntersectsPolygon(home, firstPt, nfz.vertices, nfz.bufferM ?? 0)) {
          errors.push({
            code: 'route-crosses-nfz',
            scope: { kind: 'waypoint', waypointId: wps[0].id },
            message: `Launch leg (dock → WP1): crosses no-fly zone "${nfz.name ?? 'unnamed'}".`,
          });
          break;
        }
      }
      const last = wps[wps.length - 1];
      const lastPt: [number, number] = [last.lng, last.lat];
      for (const nfz of nfzs) {
        if (segmentIntersectsPolygon(lastPt, home, nfz.vertices, nfz.bufferM ?? 0)) {
          errors.push({
            code: 'route-crosses-nfz',
            scope: { kind: 'waypoint', waypointId: last.id },
            message: `Return leg (WP${wps.length} → dock): crosses no-fly zone "${nfz.name ?? 'unnamed'}".`,
          });
          break;
        }
      }
    }
  }

  for (let i = 0; i < wps.length - 1; i++) {
    const wp = wps[i];
    const next = wps[i + 1];
    const lp = wp.legParams;
    if (!lp) continue;

    // Gimbal pitch range. Yaw is normalized at export so no error here.
    if (lp.gimbalPitch !== undefined && (lp.gimbalPitch < -90 || lp.gimbalPitch > 30)) {
      errors.push({
        code: 'pitch-out-of-range',
        scope: { kind: 'leg', waypointId: wp.id, legIndex: i },
        message: `Leg ${i + 1}→${i + 2}: gimbal pitch ${lp.gimbalPitch}° outside DJI range [-90, 30].`,
      });
    }

    if (lp.intervalPhoto?.enabled) {
      if (!Number.isFinite(lp.intervalPhoto.distanceM) || lp.intervalPhoto.distanceM <= 0) {
        errors.push({
          code: 'interval-distance-invalid',
          scope: { kind: 'leg', waypointId: wp.id, legIndex: i },
          message: `Leg ${i + 1}→${i + 2}: interval distance must be greater than 0 m.`,
        });
      }
      if (!lp.intervalPhoto.lenses || lp.intervalPhoto.lenses.length === 0) {
        errors.push({
          code: 'interval-no-lenses',
          scope: { kind: 'leg', waypointId: wp.id, legIndex: i },
          message: `Leg ${i + 1}→${i + 2}: interval shooting enabled but no lens selected.`,
        });
      }
    }

    if (lp.recordVideo && lp.recordVideoLens && lp.intervalPhoto?.enabled) {
      // M3T hardware: a lens recording video cannot simultaneously capture
      // stills. Block the user from generating a WPML that would either be
      // rejected by Pilot 2 or skip half the photos on the actual flight.
      if (lp.intervalPhoto.lenses.includes(lp.recordVideoLens)) {
        errors.push({
          code: 'lens-conflict-interval',
          scope: { kind: 'leg', waypointId: wp.id, legIndex: i },
          message: `Leg ${i + 1}→${i + 2}: recording lens '${lp.recordVideoLens}' conflicts with interval-photo lens. Move one to a different sensor.`,
        });
      }
    }

    // Same shutter-collision rule against the per-waypoint 'photo' action
    // at either end of the leg.
    const adjacentPhotos: Array<[DemoWaypoint, 'start' | 'end']> = [
      [wp, 'start'],
      [next, 'end'],
    ];
    for (const [adjacent, position] of adjacentPhotos) {
      if (adjacent.action !== 'photo') continue;
      const lensSet = adjacent.photoPayload && adjacent.photoPayload.length > 0
        ? adjacent.photoPayload
        : ['wide'];
      if (lp.recordVideo && lp.recordVideoLens && lensSet.includes(lp.recordVideoLens)) {
        errors.push({
          code: 'lens-conflict-waypoint-photo',
          scope: { kind: 'leg', waypointId: wp.id, legIndex: i },
          message: `Leg ${i + 1}→${i + 2}: recording lens '${lp.recordVideoLens}' is also used by the '${position === 'start' ? `WP${i + 1}` : `WP${i + 2}`}' photo action.`,
        });
      }
    }
  }

  return errors;
}

/** Convenience: check whether the mission is in an exportable state. Pass
 *  the bound dock to include geofence + NFZ checks. */
export function isMissionValid(
  mission: DemoMission,
  dock?: DemoDockStation | null,
): boolean {
  return validateMission(mission, dock).length === 0;
}

/** Resolve the effective leg params after carry-over from preceding legs.
 *  Lets the exporter / simulator / map markers read "what this leg actually
 *  is" without re-implementing the carry-over scan everywhere. */
export function resolveEffectiveLegParams(
  waypoints: DemoWaypoint[],
  legIndex: number,
): DemoLegParams {
  // Walk back through prior legs accumulating the most-recent value of each
  // field. The shallow merge is intentional — intervalPhoto is a leaf, not
  // a nested merge target.
  const out: DemoLegParams = {};
  for (let i = 0; i <= legIndex && i < waypoints.length - 1; i++) {
    const lp = waypoints[i].legParams;
    if (!lp) continue;
    if (lp.gimbalPitch !== undefined) out.gimbalPitch = lp.gimbalPitch;
    if (lp.gimbalYaw !== undefined) out.gimbalYaw = lp.gimbalYaw;
    if (lp.recordVideo !== undefined) out.recordVideo = lp.recordVideo;
    if (lp.recordVideoLens !== undefined) out.recordVideoLens = lp.recordVideoLens;
    if (lp.intervalPhoto !== undefined) out.intervalPhoto = lp.intervalPhoto;
  }
  return out;
}
